import { Canvas } from '@react-three/fiber';
import { KeyboardControls } from '@react-three/drei';
import { Suspense, type ReactNode } from 'react';
import { SceneGraph } from './SceneGraph';
import { NavRig } from '../navigation/NavRig';
import { FOG_COLOR, FOG_NEAR, FOG_FAR, CAMERA_HEIGHT } from '../lib/const';

/**
 * WASD + arrow keys, mapped for drei's <useKeyboardControls> hook.
 * Plan 03's WalkRig reads this map via getKeys() on every useFrame tick.
 */
export const keyMap = [
  { name: 'forward', keys: ['ArrowUp', 'w', 'W'] },
  { name: 'backward', keys: ['ArrowDown', 's', 'S'] },
  { name: 'left', keys: ['ArrowLeft', 'a', 'A'] },
  { name: 'right', keys: ['ArrowRight', 'd', 'D'] },
];

/**
 * R3F Canvas wrapper that sets up the painterly-matte rendering environment.
 *
 * AESTHETIC-BRIEF choices:
 *   - `shadows={false}` per Rule 2 (real-time shadows restricted to player at <5m,
 *     a Phase 5+ concern — Phase 3 turns them off entirely).
 *   - `<color>` + `<fog>` instead of `<Sky>` / HDRI per Rule 4 (painted gradient sky).
 *     Phase 4 may replace the flat color with a vertical gradient shader.
 *   - Fog near/far per Rule 5 (warm-ochre tint, 200m far distance).
 *
 * Camera defaults: 70° FOV, 1.7m eye height, starts 5m south of origin looking
 * north. Plan 03's WalkRig takes over camera position via useFrame.
 *
 * The `children` prop lets App.tsx inject a <NavController /> (Plan 03) inside
 * the Canvas without this module knowing about navigation.
 */
export function Renderer({ children }: { children?: ReactNode }) {
  return (
    <KeyboardControls map={keyMap}>
      <Canvas
        shadows={false}
        camera={{
          fov: 70,
          near: 0.1,
          far: 1000,
          position: [0, CAMERA_HEIGHT, 5],
        }}
        gl={{ antialias: true, powerPreference: 'high-performance' }}
        style={{ position: 'fixed', inset: 0 }}
      >
        <color attach="background" args={[FOG_COLOR]} />
        <fog attach="fog" args={[FOG_COLOR, FOG_NEAR, FOG_FAR]} />
        <Suspense fallback={null}>
          <SceneGraph />
          <NavRig />
          {children}
        </Suspense>
      </Canvas>
    </KeyboardControls>
  );
}
